import React, { useState, useEffect, useMemo } from 'react';
import css from './main.module.css';
import { useTranslation } from 'react-i18next';
import thunder from "../../img/thunder.png";
import { Link } from 'react-router-dom';
import { toggleVideoFavorite } from '../../lib/favoriteApi';
import { getLanguageLevels } from "../../lib/getLanguageLevels";
import { useNavigate } from 'react-router-dom';
import { getLearningPaths } from '../../lib/learningPath';

const UserAccount = ({ authUser }) => {
  const { t, i18n } = useTranslation();
  const [activeMyVideosTab, setActiveMyVideosTab] = useState('all my videos');
  const [videos, setVideos] = useState(authUser?.videoLessonsAll || []);
  const [favoriteTogglingIds, setFavoriteTogglingIds] = useState(new Set());
  const [languageLevels, setLanguageLevels] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(true);

  // Remove video states
  const [removeTarget, setRemoveTarget] = useState(null);
  const [showRemoveToast, setShowRemoveToast] = useState(false);

  const confirmRemove = () => {
    if (removeTarget) {
      setVideos(videos.filter(v => v.id !== removeTarget.id));
      setRemoveTarget(null);
      setShowRemoveToast(true);
      setTimeout(() => setShowRemoveToast(false), 3500);
    }
  };

  const navigate = useNavigate();

  // Use state-managed video data
  const allVideos = videos;
  const completedVideos = videos.filter(v => v.is_completed);
  const startedVideos = videos.filter(v => v.is_started && !v.is_completed);
  const favoriteVideos = videos.filter(v => v.is_favorited);

  // Tab counts
  const totalVideosAmount = allVideos.length;
  const videosCompletedAmount = completedVideos.length;
  const inProgressAmount = startedVideos.length;
  const favoredAmount = favoriteVideos.length;

  // Stats
  const wordsLearned = authUser?.stats.vocabulary_count || 0;
  const questionsAnswered = authUser?.stats.correct_answers || 0;
  const dialoguesPlayed = authUser?.stats.dialogues_role_played_correctly || 0;
  const daysRemaining = authUser?.stats.subscription_remaining_days || 0;

  // Filtered videos for current tab
  let filteredVideos = allVideos;
  if (activeMyVideosTab === 'inProgress') filteredVideos = startedVideos;
  if (activeMyVideosTab === 'completed') filteredVideos = completedVideos;
  if (activeMyVideosTab === 'favorites') filteredVideos = favoriteVideos;

  console.log(authUser)
  // Level mapping for different language levels
  const levelMapping = useMemo(
    () => ({
      A1: {
        fullName: t("levelonea"),
        order: 1,
        bgColor: "#e8f5e9",
        textColor: "#2e7d32",
        name: "A1",
      },
      A2: {
        fullName: t("leveltwoa"),
        order: 2,
        bgColor: "#fff3e0",
        textColor: "#f57c00",
        name: "A2",
      },
      B1: {
        fullName: t("levevoneb"),
        order: 3,
        bgColor: "#e3f2fd",
        textColor: "#1565c0",
        name: "B1",
      },
      B2: {
        fullName: t("lebeltwob"),
        order: 4,
        bgColor: "#ede7f6",
        textColor: "#673ab7",
        name: "B2",
      },
      C1: {
        fullName: t("levelonec"),
        order: 5,
        bgColor: "#ffebee",
        textColor: "#d32f2f",
        name: "C1",
      },
      C2: {
        fullName: t("leveltwoc"),
        order: 6,
        bgColor: "#fce4ec",
        textColor: "#c2185b",
        name: "C2",
      },
    }),
    [i18n.language]
  );

  // Update videos state when authUser changes
  useEffect(() => {
    if (authUser?.videoLessonsAll) {
      setVideos(authUser.videoLessonsAll);
    }
  }, [authUser]);

  useEffect(() => {
    // Fetch language levels and learning paths when component mounts
    const fetchData = async () => {
      try {
        const levelsResponse = await getLanguageLevels();
        const levelsArray = Array.isArray(levelsResponse) ? levelsResponse : [];
        setLanguageLevels(levelsArray);
        localStorage.setItem("languageLevels", JSON.stringify(levelsArray));

        // Fetch learning paths
        setIsLoadingPaths(true);
        const storedLevel = localStorage.getItem("selectedLevel");
        const storedPurpose = localStorage.getItem("selectedPurpose");
        
        const filters = {};
        if (storedLevel) {
          // Convert level to the format expected by API (questionnaire ID)
          filters.level = parseInt(storedLevel);
        }
        if (storedPurpose) {
          try {
            const purposeData = JSON.parse(storedPurpose);
            if (purposeData.id) {
              filters.purpose = purposeData.id;
            }
          } catch (e) {
            console.error("Error parsing stored purpose:", e);
          }
        }

        const pathsResponse = await getLearningPaths(filters);
        setLearningPaths(pathsResponse.results || []);
      } catch (err) {
        console.error("❌ Error fetching data:", err);
      } finally {
        setIsLoadingPaths(false);
      }
    };
    fetchData();
  }, []);

  // Handle favorite toggle
  const handleFavoriteToggle = async (e, videoLessonId) => {
    e.preventDefault(); // Prevent navigation to lesson page
    e.stopPropagation(); // Stop event bubbling

    // Prevent multiple clicks on the same video
    if (favoriteTogglingIds.has(videoLessonId)) return;

    // Add to toggling set
    setFavoriteTogglingIds(prev => new Set(prev).add(videoLessonId));

    // Find the current video to get its favorite status and ID
    const currentVideo = videos.find(video => video.id === videoLessonId);
    const currentFavoriteId = currentVideo?.favorite_id;
    const isFavorited = currentVideo?.is_favorited;

    // Optimistic update - update UI immediately
    const previousVideos = videos;
    setVideos(prevVideos =>
      prevVideos.map(video =>
        video.id === videoLessonId
          ? {
            ...video,
            is_favorited: !video.is_favorited,
            favorite_id: !video.is_favorited ? 'temp_id' : null // Temporary ID, will be updated with real ID from API
          }
          : video
      )
    );

    try {
      const response = await toggleVideoFavorite(videoLessonId, currentFavoriteId, isFavorited);

      // Update with the real favorite ID from the API response
      if (!isFavorited) {
        // Adding to favorites - update with real ID from response
        setVideos(prevVideos =>
          prevVideos.map(video =>
            video.id === videoLessonId
              ? {
                ...video,
                favorite_id: response.id
              }
              : video
          )
        );
      }
      // For removing favorites, the optimistic update already set favorite_id to null

      console.log('Favorite status updated successfully for video:', videoLessonId);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert the optimistic update on error
      setVideos(previousVideos);
      // Show user-friendly error message
      alert(t('Failed to update favorite status. Please try again.'));
    } finally {
      // Remove from toggling set
      setFavoriteTogglingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(videoLessonId);
        return newSet;
      });
    }
  };

  // Map learning paths to course format
  const courses = useMemo(() => {
    if (!learningPaths || learningPaths.length === 0) {
      // Fallback to hardcoded courses if no paths available
      return [
        {
          id: 1,
          name: "Business English",
          level: "Intermediate",
          totalLessons: 24,
          completedLessons: 18,
          wordsLearned: 245,
          progress: 72,
          icon: "💼",
          iconBg: "#6B46C1"
        },
        {
          id: 2,
          name: "Conversational English",
          level: "Beginner",
          totalLessons: 30,
          completedLessons: 13,
          wordsLearned: 156,
          progress: 43,
          icon: "💬",
          iconBg: "#9F7AEA"
        },
        {
          id: 3,
          name: "Travel English",
          level: "Intermediate",
          totalLessons: 15,
          completedLessons: 3,
          wordsLearned: 42,
          progress: 20,
          icon: "✈️",
          iconBg: "#667eea"
        }
      ];
    }

    // Map learning paths from API to course format
    return learningPaths.map((path, index) => {
      // Get icon based on purpose
      const purposeName = path.purpose?.purpose?.toLowerCase() || '';
      let icon = "📚";
      let iconBg = "#6B46C1";
      
      if (purposeName.includes('travel') || purposeName.includes('подорож') || purposeName.includes('podróż')) {
        icon = "✈️";
        iconBg = "#667eea";
      } else if (purposeName.includes('job') || purposeName.includes('work') || purposeName.includes('business') || purposeName.includes('робот') || purposeName.includes('praca')) {
        icon = "💼";
        iconBg = "#6B46C1";
      } else if (purposeName.includes('education') || purposeName.includes('освіт') || purposeName.includes('edukac')) {
        icon = "🎓";
        iconBg = "#9F7AEA";
      } else if (purposeName.includes('communication') || purposeName.includes('спілкування') || purposeName.includes('komunikac')) {
        icon = "💬";
        iconBg = "#9F7AEA";
      } else if (purposeName.includes('entertainment') || purposeName.includes('розваг') || purposeName.includes('fun')) {
        icon = "🎬";
        iconBg = "#667eea";
      }

      // Parse topics to count lessons
      let totalLessons = 0;
      try {
        const topics = typeof path.topics === 'string' ? JSON.parse(path.topics) : path.topics;
        if (Array.isArray(topics)) {
          topics.forEach(topic => {
            if (topic.lessons && Array.isArray(topic.lessons)) {
              totalLessons += topic.lessons.length;
            }
          });
        }
      } catch (e) {
        console.error("Error parsing topics:", e);
        totalLessons = 0;
      }

      return {
        id: path.id,
        name: path.name,
        level: path.level?.option || "Unknown",
        totalLessons: totalLessons || 20, // Default to 20 if can't parse
        completedLessons: 0, // TODO: Get from user progress when available
        wordsLearned: 0, // TODO: Get from user progress when available
        progress: 0, // TODO: Calculate from user progress when available
        icon,
        iconBg,
        pathData: path // Store original path data for navigation
      };
    });
  }, [learningPaths]);

  return (
    <div className={css.newCabinetWrap}>
      {/* Welcome Section */}
      <section className={css.welcomeSection}>
        <h1 className={css.welcomeTitle}>{t('Welcome back')}, {authUser?.name || 'User'}!</h1>
        <div className={css.statsGrid}>
          <div className={css.statCard}>
            <div className={css.statNumber}>{Math.max(0, totalVideosAmount - videosCompletedAmount)}</div>
            <div className={css.statLabel}>{t("Lessons left", "Уроків залишилося")}</div>
          </div>
          <div className={css.statCard}>
            <div className={css.statNumber}>{wordsLearned}</div>
            <div className={css.statLabel}>{t("Words learned", "Слів у словнику")}</div>
          </div>
          <div className={css.statCard}>
            <div className={css.statNumber}>{videosCompletedAmount}</div>
            <div className={css.statLabel}>{t("Lessons completed", "Уроків завершено")}</div>
          </div>
        </div>
      </section>

      {/* My Courses Section */}
      <section className={css.coursesSection}>
        <div className={css.coursesHeader}>
          <h2 className={css.sectionTitle}>
            <div className={css.sectionIcon}>📖</div>
            {t("My Courses")}
          </h2>
          <a href="#" className={css.viewAll}>{t("View all courses")} →</a>
        </div>
        <div className={css.coursesGrid}>
          {isLoadingPaths ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
              <p>{t("Loading courses...")}</p>
            </div>
          ) : courses.length === 0 ? (
            <div style={{ 
              gridColumn: '1 / -1', 
              background: '#fff', 
              borderRadius: '20px', 
              padding: '40px', 
              textAlign: 'center', 
              border: '1px dashed #cbd5e1',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)' 
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '8px' }}>
                {t('userAccount.emptyPlanTitle', 'У вас ще немає персонального плану')}
              </h3>
              <p style={{ color: '#64748b', fontSize: '15px', maxWidth: '400px', margin: '0 auto 24px' }}>
                {t('userAccount.emptyPlanDesc', 'Створіть свій перший навчальний план, щоб ефективно досягати цілей у вивченні англійської.')}
              </p>
              <button 
                onClick={() => navigate('/survey')}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('userAccount.emptyPlanBtn', 'Створити мій план')}
              </button>
            </div>
          ) : (
            courses.map((course) => (
              <div 
                onClick={() => { 
                  // Store the learning path ID for the roadmap
                  if (course.pathData) {
                    localStorage.setItem('currentLearningPathId', course.id);
                  }
                  navigate('/roadMap');
                }} 
                key={course.id} 
                className={css.courseCard}
              >
              <div className={css.courseHeader}>
                <div
                  className={css.courseIcon}
                  style={{ background: course.iconBg }}
                >
                  {course.icon}
                </div>
                <div className={css.courseInfo}>
                  <h3>{course.name}</h3>
                  <span className={css.courseLevel}>
                    {course.level} • {course.totalLessons} {t("lessons")}
                  </span>
                </div>
              </div>
              <div className={css.courseProgress}>
                <div className={css.progressInfo}>
                  <span>{t("Progress")}</span>
                  <span>{course.progress}%</span>
                </div>
                <div className={css.progressBar}>
                  <div
                    className={css.progressFill}
                    style={{ width: `${course.progress}%` }}
                  ></div>
                </div>
              </div>
              <div className={css.courseStats}>
                <div className={css.stat}>
                  <div className={css.statValue}>{course.completedLessons}</div>
                  <div className={css.statDesc}>{t("Lessons done")}</div>
                </div>
                <div className={css.stat}>
                  <div className={css.statValue}>{course.wordsLearned}</div>
                  <div className={css.statDesc}>{t("Words learned")}</div>
                </div>
                <div className={css.stat}>
                  <div className={css.statValue}>{course.totalLessons - course.completedLessons}</div>
                  <div className={css.statDesc}>{t("Lessons left")}</div>
                </div>
              </div>
            </div>
            ))
          )}
        </div>
      </section>

      {/* My Videos Section */}
      <section className={css.videosSection}>
        <div className={css.coursesHeader}>
          <h2 className={css.sectionTitle} style={{ fontSize: '20px', color: '#333' }}>
            <div className={css.sectionIcon} style={{ background: '#6B46C1', color: 'white', width: '28px', height: '28px', fontSize: '16px' }}>🎬</div>
            {t("My Videos")}
          </h2>
        </div>
        <div className={css.videosTabs}>
          <button
            className={`${css.tab} ${activeMyVideosTab === 'all my videos' ? css.active : ''}`}
            onClick={() => setActiveMyVideosTab('all my videos')}
          >
            {t("All My Videos")} <span className={css.tabBadge}>{totalVideosAmount}</span>
          </button>
          <button
            className={`${css.tab} ${activeMyVideosTab === 'inProgress' ? css.active : ''}`}
            onClick={() => setActiveMyVideosTab('inProgress')}
          >
            {t("In Progress")} <span className={`${css.tabBadge}`} style={{ background: '#666' }}>{inProgressAmount}</span>
          </button>
          <button
            className={`${css.tab} ${activeMyVideosTab === 'completed' ? css.active : ''}`}
            onClick={() => setActiveMyVideosTab('completed')}
          >
            {t("Completed")} <span className={`${css.tabBadge}`} style={{ background: '#666' }}>{videosCompletedAmount}</span>
          </button>
          <button
            className={`${css.tab} ${activeMyVideosTab === 'favorites' ? css.active : ''}`}
            onClick={() => setActiveMyVideosTab('favorites')}
          >
            {t("❤️ Favorites")} <span className={`${css.tabBadge}`} style={{ background: '#666' }}>{favoredAmount}</span>
          </button>
        </div>
        <div className={css.videosGrid}>
          {filteredVideos.length > 0 ? (
            filteredVideos.map((videoObj, index) => {
              const video = videoObj.video;
              return (
                <Link to={`/lesson/${videoObj.id}`} key={index} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className={css.videoCard}>
                    {/* Video thumbnail */}
                    <div className={css.videoThumbnailContainer}>
                      <img
                        src={`http://staging-api.ailingua.net/` + video.thumbnail}
                        alt={video.name}
                        className={css.videoThumbnail}
                      />
                    </div>
                    {/* Video card content */}
                    <h3>{video.name}</h3>
                    <div className={css.videoCardFooter}>
                      {(() => {
                        // Level badge and title
                        const levelIdToCode = {
                          1: 'A1', 2: 'A2', 3: 'B1', 4: 'B2', 5: 'C1', 6: 'C2'
                        };
                        const levelCode = levelIdToCode[video.languageLevel];
                        const levelObj = levelMapping[levelCode];
                        const levelStyle = levelCode && levelObj ? {
                          backgroundColor: levelObj.bgColor,
                          color: levelObj.textColor,
                          border: `1px solid ${levelObj.textColor}`
                        } : {};
                        return (
                          <>
                            {levelObj && (
                              <span style={{ color: levelObj.textColor, fontWeight: 600, marginLeft: 8 }}>
                                {levelObj.fullName}
                              </span>
                            )}

                            <span
                              className={css.levelBadge}
                              style={levelStyle}
                            >
                              {levelCode || t("unknownLevel")}
                            </span>
                          </>
                        );
                      })()}
                      {/* Progress bar */}
                      {videoObj.progress_percentage < 100 && (
                        <div className={css.progressBar}>
                          <div
                            className={css.progressFill}

                            style={{ width: videoObj.progress_percentage > 2 ? `${videoObj.progress_percentage}%` : 2 }}
                          ></div>
                        </div>
                      )}
                      {videoObj.is_completed && (
                        <span className={css.completedBadge}>{t("Completed")}</span>
                      )}
                      <div className={css.cardBottom}>
                        <p>{videoObj.vocabulary_count}  {videoObj.vocabulary_count === 1 ? t("word") : t("words")}</p>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button 
                            className={css.vcRemove} 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setRemoveTarget({ id: videoObj.id, title: video.name });
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                          </button>
                          <span
                            className={css.favoriteBadge}
                            onClick={(e) => handleFavoriteToggle(e, videoObj.id)}
                            style={{
                              cursor: favoriteTogglingIds.has(videoObj.id) ? 'wait' : 'pointer',
                              opacity: favoriteTogglingIds.has(videoObj.id) ? 0.6 : 1
                            }}
                          >
                            {videoObj.is_favorited ? '❤️' : '🤍'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>

              );
            })
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6d6d6d', gridColumn: '1 / -1' }}>
              <p>{t("No videos found")}</p>
            </div>
          )}
        </div>
      </section>
      {/*<Link to={`/lesson/${videoObj.id}`} key={index} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className={css.videoCard}>
                    <div className={css.videoThumbnail}>
                      <img 
                        src={`http://staging-api.ailingua.net/` + video.thumbnail}
                        alt={video.name} 
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <span className={css.videoDuration}>
                        {Math.floor(Math.random() * 15) + 5}:{Math.floor(Math.random() * 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                    <div className={css.videoInfo}>
                      <h3 className={css.videoTitle}>{video.name}</h3>
                      <div className={css.videoMeta}>
                        {(() => {
                          const levelIdToCode = {
                            1: 'A1', 2: 'A2', 3: 'B1', 4: 'B2', 5: 'C1', 6: 'C2'
                          };
                          const levelCode = levelIdToCode[video.languageLevel];
                          const levelObj = levelMapping[levelCode];
                          return levelObj ? (
                            <span style={{ color: levelObj.textColor, fontWeight: 600, fontSize: '12px' }}>
                              {levelObj.fullName}
                            </span>
                          ) : null;
                        })()}
                        {videoObj.progress_percentage < 100 && (
                          <div className={css.progressBar} style={{ marginTop: '8px', height: '4px' }}>
                            <div 
                              className={css.progressFill} 
                              style={{ width: videoObj.progress_percentage > 2 ? `${videoObj.progress_percentage}%` : '2%' }}
                            ></div>
                          </div>
                        )}
                        {videoObj.is_completed && (
                          <span className={css.completedBadge} style={{ fontSize: '10px', marginTop: '4px' }}>{t("Completed")}</span>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {videoObj.vocabulary_count} {videoObj.vocabulary_count === 1 ? t("word") : t("words")}
                          </span>
                          <span 
                            onClick={(e) => handleFavoriteToggle(e, videoObj.id)}
                            style={{ 
                              cursor: favoriteTogglingIds.has(videoObj.id) ? 'wait' : 'pointer',
                              opacity: favoriteTogglingIds.has(videoObj.id) ? 0.6 : 1,
                              fontSize: '16px'
                            }}
                          >
                            {videoObj.is_favorited ? '❤️' : '🤍'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>*/}
      {/* Remove Modal */}
      {removeTarget && (
        <div className={`${css.rmOv} ${css.show}`} onClick={(e) => { if (e.target === e.currentTarget) setRemoveTarget(null); }}>
          <div className={css.rmModal}>
            <div className={css.rmX} onClick={() => setRemoveTarget(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </div>
            <div className={css.rmIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
            </div>
            <div className={css.rmH}>{t('removeVideoTitle', 'Видалити відео з кабінету?')}</div>
            <div className={css.rmP}>{t('removeVideoDesc', 'Відео буде видалено з розділу «Мої відео». Ви зможете знайти його знову на сторінці «Усі відео».')}</div>
            <div className={css.rmCtx}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              <span>{removeTarget.title}</span>
            </div>
            <div className={css.rmBtns}>
              <button className={`${css.rmBtn} ${css.rmBtnCancel}`} onClick={() => setRemoveTarget(null)}>
                {t('cancel', 'Скасувати')}
              </button>
              <button className={`${css.rmBtn} ${css.rmBtnDel}`} onClick={confirmRemove}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/></svg>
                {t('remove', 'Видалити')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <div className={`${css.toast} ${showRemoveToast ? css.show : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        <div>
          {t('videoRemoved', 'Відео видалено з кабінету')}
          <div className={css.toastSub}>{t('videoRemovedDesc', 'Ви можете знайти його на сторінці «Усі відео».')}</div>
        </div>
      </div>

    </div>
  );
};

export default UserAccount;